import { useParams } from '@tanstack/react-router';
import {
  InputMaybe,
  SocialAccountType,
  namedOperations,
  useConnectPackageWithSocialAccountsMutation,
  useDeleteSocialAccountPackageMutation
} from '@anytag/graphql';
import { useQueryHelper } from '@anytag/shared-hooks';
import { SelectedSocialMediaAccountsType } from '@anytag/shared-organisms';
import { getSocialPairsIds } from '@anytag/shared-utils';

interface ToggleSocialAccountProps {
  isChecked?: boolean;
  selectedSocialMediaAccounts: SelectedSocialMediaAccountsType[];
  socialAccountIds: number[];
  readonly socialType?: InputMaybe<SocialAccountType>;
}

export const useToggleSelectedSocialAccountsForPackage = () => {
  const { t, enqueueSnackbar } = useQueryHelper();
  const params = useParams({ from: '/_private-routes/package/$id/content/search' });

  const { callConnectPackageWithSocialAccounts, loading: isUpdatingProposal } =
    useConnectPackageWithSocialAccountsMutation({
      refetchQueries: [namedOperations.Query.PackageProposal, namedOperations.Query.CheckInfluencersAddedToPackage]
    });
  const { callDeleteSocialAccountPackage, loading: isDeletingProposal } = useDeleteSocialAccountPackageMutation({
    refetchQueries: [namedOperations.Query.PackageProposal, namedOperations.Query.CheckInfluencersAddedToPackage]
  });

  const toggleSocialMediaSelectedInfluencer = async ({
    isChecked,
    socialAccountIds,
    socialType,
    selectedSocialMediaAccounts
  }: ToggleSocialAccountProps) => {
    let error;
    const { socialPairs } = getSocialPairsIds(
      selectedSocialMediaAccounts.map((acc) => ({
        ...acc,
        socialAccountMedia: acc.socialAccountType,
        socialAccountId: Number(acc.socialAccountId)
      }))
    );
    const packageId = Number(params.id);

    try {
      if (isChecked) {
        await callDeleteSocialAccountPackage({
          variables: {
            packageId,
            socialAccountIds,
            socialMedia: socialType as SocialAccountType
          }
        });
      } else {
        await callConnectPackageWithSocialAccounts({
          variables: {
            input: {
              socialPairs,
              packageId
            }
          }
        });
      }
      // we don't want to notify user on each save/delete, just show an error if happens
    } catch (err) {
      error = err.message;
      enqueueSnackbar(t(err.message), { variant: 'error' });
    }

    return { error };
  };

  return { toggleSocialMediaSelectedInfluencer, loading: isUpdatingProposal || isDeletingProposal };
};
